import type { PageContent } from "@/lib/pages/schema";

type Saved = { src: string | null; srcset: string | null; alt: string | null; applied: string };

/**
 * Replaces built-in images (matched by asset file name, including Vite hash suffixes) only inside `root`,
 * and returns a cleanup that restores every touched image's original src/srcset/alt exactly.
 * Images outside the managed page or preview are never modified.
 */
export function applyMediaOverrides(root: Element | null, overrides: PageContent["media_overrides"] | undefined): () => void {
  const entries = Object.entries(overrides ?? {});
  if (!root || !entries.length) return () => {};
  const rules = entries.map(([name, v]) => {
    const dot = name.lastIndexOf(".");
    const stem = (dot > 0 ? name.slice(0, dot) : name).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const ext = dot > 0 ? name.slice(dot + 1).replace(/[.*+?^${}()|[\]\\]/g, "\\$&") : "\\w+";
    return { re: new RegExp(`/${stem}(-[A-Za-z0-9_-]{6,})?\\.${ext}(\\?.*)?$`), v };
  });
  const saved = new Map<Element, Saved>();

  const restore = (el: Element, s: Saved) => {
    const set = (attr: string, value: string | null) => (value === null ? el.removeAttribute(attr) : el.setAttribute(attr, value));
    set("src", s.src); set("srcset", s.srcset); set("alt", s.alt);
  };
  const applyOne = (el: Element) => {
    const prev = saved.get(el);
    if (prev) {
      if (el.getAttribute("src") === prev.applied) return;
      saved.delete(el); // the app re-rendered this image with a new source; treat it as a fresh original
    }
    const src = el.getAttribute("src") ?? "";
    const rule = rules.find((r) => r.re.test(src));
    if (!rule) return;
    saved.set(el, { src: el.getAttribute("src"), srcset: el.getAttribute("srcset"), alt: el.getAttribute("alt"), applied: rule.v.url });
    el.removeAttribute("srcset");
    el.setAttribute("src", rule.v.url);
    if (rule.v.alt) el.setAttribute("alt", rule.v.alt);
  };
  const applyAll = () => {
    if (root.tagName === "IMG") applyOne(root);
    root.querySelectorAll("img").forEach(applyOne);
  };
  applyAll();
  const MO = (root.ownerDocument?.defaultView as unknown as { MutationObserver?: typeof MutationObserver } | null)?.MutationObserver
    ?? (typeof MutationObserver !== "undefined" ? MutationObserver : undefined);
  const obs = MO ? new MO(applyAll) : null;
  obs?.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ["src"] });

  return () => {
    obs?.disconnect();
    saved.forEach((s, el) => { if (el.getAttribute("src") === s.applied) restore(el, s); });
    saved.clear();
  };
}
